import { SearchWidget } from '@/features/search/components/SearchWidget';
import { usePageSection } from '@/hooks/usePageContent';
import { HeroPhotoRotator, type HeroPhoto } from './HeroPhotoRotator';

interface HeroContent {
    heading: string;
    subheading: string;
}

const DEFAULT_HERO: HeroContent = {
    heading: 'Car seats, strollers and cots to rent in Spain and Ireland',
    subheading:
        'Pick a location and your dates. Your gear is cleaned and safety-checked before every rental and ready for collection when you arrive. Rent by the day for a holiday, or by the month at home.',
};

/** Photos carried over from the original storkrentals.ie hero slider. */
const HERO_PHOTOS: HeroPhoto[] = [
    {
        src: '/assets/images/home/hero-car-seat.jpg',
        webp: '/assets/images/home/hero-car-seat.webp',
        alt: 'A smiling baby buckled into one of our rental car seats',
        position: '60% center',
    },
    {
        src: '/assets/images/home/hero-airport-stroller.jpg',
        webp: '/assets/images/home/hero-airport-stroller.webp',
        alt: 'A mother leaning over a rental stroller at an airport gate',
        position: '55% center',
    },
    {
        src: '/assets/images/home/hero-beach-stroller.jpg',
        webp: '/assets/images/home/hero-beach-stroller.webp',
        alt: 'A mother pushing a rental stroller along a beach at sunset',
        position: '42% center',
    },
];

export const HeroSection = () => {
    const cms = usePageSection<HeroContent>('home', 'hero');
    const content = cms ?? DEFAULT_HERO;

    return (
        <section className="relative isolate overflow-hidden bg-[linear-gradient(180deg,rgba(246,246,233,1)_0%,rgba(242,238,208,0.98)_56%,rgba(246,246,233,1)_100%)] pb-20 pt-8 sm:pb-18 sm:pt-12 lg:pb-24 lg:pt-14">

            {/* Photos stay behind the content at every width; the mobile cream
                wash keeps text readable without adding a separate image band. */}
            <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-xl">
                    <p className="delight-rise font-display text-lg italic text-primary sm:text-xl">
                        Clean, Safe and Convenient
                    </p>
                    <h1 className="delight-rise mt-3 font-display text-[clamp(2.6rem,5.4vw,4.5rem)] leading-[0.96] tracking-tight text-foreground">
                        {content.heading}
                    </h1>

                    <p className="delight-rise delight-delay-1 mt-5 text-base leading-7 text-muted-foreground sm:text-lg">
                        {content.subheading}
                    </p>
                </div>

                <div className="delight-rise delight-delay-2 mt-8 w-full sm:mt-10 lg:mt-12">
                    <SearchWidget variant="hero" />
                </div>
            </div>

            <figure
                data-testid="hero-photo"
                className="absolute inset-0 z-0 lg:left-auto lg:w-[52%] lg:[-webkit-mask-image:linear-gradient(to_right,transparent_0%,transparent_15%,#000_50%)] lg:[mask-image:linear-gradient(to_right,transparent_0%,transparent_15%,#000_50%)] xl:w-[58%]"
            >
                <HeroPhotoRotator
                    photos={HERO_PHOTOS}
                    variant="fill"
                    imageClassName="opacity-40 lg:opacity-100"
                    controlsClassName="z-20 lg:bottom-5 lg:right-6"
                />
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(100deg,rgba(246,246,233,0.92)_0%,rgba(246,246,233,0.7)_45%,rgba(246,246,233,0.2)_100%)] lg:hidden" />
            </figure>
        </section>
    );
};
